import * as React from "react";
import dayjs from "dayjs";
import { InputPrefix, InputSuffix } from "@progress/kendo-react-inputs";
import { Box, CPText } from "shared/components/base";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import {
  PendingUserInvite,
  useCancelUserInviteMutation,
  useResendUserInviteMutation,
  useSearchUserInvitesQuery,
} from "data/users/userInvites";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";
import useAppState from "shared/state/useAppState";
import "./PendingInvitesPanelStyles.scss";

interface PendingInvitesPanelProps {
  open: boolean;
  onClose: () => void;
}

const parseUtcDate = (value: string): dayjs.Dayjs =>
  dayjs(value.endsWith("Z") ? value : `${value}Z`);

const formatExpiresIn = (expiresAt: string): string => {
  const diffMinutes = parseUtcDate(expiresAt).diff(dayjs(), "minute");
  if (diffMinutes <= 0) return "expired";
  if (diffMinutes < 60) return `in ${diffMinutes} min`;
  return `in ${Math.round(diffMinutes / 60)} hr`;
};

const matchesSearch = (invite: PendingUserInvite, term: string): boolean => {
  const haystack = `${invite.firstname} ${invite.lastname} ${invite.email} ${invite.agencyCode}`.toLowerCase();
  return haystack.includes(term);
};

const PendingInvitesPanel: React.FC<PendingInvitesPanelProps> = ({
  open,
  onClose,
}) => {
  const { setToastMsg } = useAppState();
  const confirm = useConfirm();
  const { data: invites, isFetching } = useSearchUserInvitesQuery(
    "admin",
    open,
  );
  const { mutate: cancelInvite, isLoading: isCancelling } =
    useCancelUserInviteMutation("admin");
  const { mutate: resendInvite, isLoading: isResending } =
    useResendUserInviteMutation("admin");
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const filteredInvites = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term === "") return invites ?? [];
    return (invites ?? []).filter((invite) => matchesSearch(invite, term));
  }, [invites, search]);

  const handleCancel = (invite: PendingUserInvite) => {
    confirm(
      (confirmed) => {
        if (!confirmed) return;

        cancelInvite(invite.id, {
          onSuccess: () => {
            setToastMsg("Invite cancelled", "success");
          },
          onError: () => {
            setToastMsg("Failed to cancel invite", "error");
          },
        });
      },
      `Cancel the invite sent to ${invite.email}? This link will no longer work.`,
    );
  };

  const handleResend = (invite: PendingUserInvite) => {
    confirm(
      (confirmed) => {
        if (!confirmed) return;

        resendInvite(invite.id, {
          onSuccess: () => {
            setToastMsg("Invite resent", "success");
          },
          onError: () => {
            setToastMsg("Failed to resend invite", "error");
          },
        });
      },
      `Resend an invite to ${invite.email}? A new link will be emailed and the expired one will stop working.`,
    );
  };

  const hasInvites = (invites?.length ?? 0) > 0;

  return (
    <CPActionsheetModal
      title="Pending Invites"
      open={open}
      onClose={onClose}
      position="right"
      width={820}
    >
      <Box flexDirection="column" spacing={12} style={{ paddingBottom: 16 }}>
        <Box className="cp-actionsheet-input-box pending-invites-search-box">
          <CPTextBox
            name="search"
            placeholder="Search by name, email, or agency..."
            value={search}
            onChange={(e) => setSearch(e.target.value?.toString() ?? "")}
            renderPrefix={() => (
              <InputPrefix>
                <i className="fa-solid fa-magnifying-glass pending-invites-search-icon" />
              </InputPrefix>
            )}
            renderSuffix={
              search
                ? () => (
                    <InputSuffix>
                      <CPButton
                        fillMode="flat"
                        size="small"
                        title="Clear search"
                        onClick={() => setSearch("")}
                      >
                        <i className="fa-solid fa-xmark" />
                      </CPButton>
                    </InputSuffix>
                  )
                : undefined
            }
          />
        </Box>

        {!isFetching && !hasInvites && (
          <Box className="cp-actionsheet-input-box">
            <CPText textStyle="subtitle" align="center">
              There are no pending invites.
            </CPText>
          </Box>
        )}

        {!isFetching && hasInvites && filteredInvites.length === 0 && (
          <Box className="cp-actionsheet-input-box">
            <CPText textStyle="subtitle" align="center">
              No invites match your search.
            </CPText>
          </Box>
        )}

        {filteredInvites.length > 0 && (
          <div className="pending-invites-table-wrapper">
            <table className="pending-invites-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Agency</th>
                  <th>Note</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map((invite) => (
                  <tr key={invite.id}>
                    <td>
                      <CPText bold>
                        {invite.firstname} {invite.lastname}
                      </CPText>
                    </td>
                    <td>
                      <CPText title={invite.email}>{invite.email}</CPText>
                    </td>
                    <td>
                      <CPText>{invite.agencyCode}</CPText>
                    </td>
                    <td>
                      {invite.note ? (
                        <CPText
                          className="pending-invites-note-cell"
                          style={{ fontStyle: "italic" }}
                          title={invite.note}
                        >
                          &ldquo;{invite.note}&rdquo;
                        </CPText>
                      ) : (
                        <CPText color="secondary">&mdash;</CPText>
                      )}
                    </td>
                    <td>
                      {invite.isExpired ? (
                        <CPText color="error" bold>
                          Expired
                        </CPText>
                      ) : (
                        <CPText color="secondary">
                          Sent by {invite.invitedByName || "you"} &middot;{" "}
                          Expires {formatExpiresIn(invite.expiresAt)}
                        </CPText>
                      )}
                    </td>
                    <td className="pending-invites-action-cell">
                      {invite.isExpired && (
                        <CPButton
                          fillMode="flat"
                          size="small"
                          themeColor="success"
                          disabled={isCancelling || isResending}
                          onClick={() => handleResend(invite)}
                        >
                          Resend
                        </CPButton>
                      )}
                      <CPButton
                        fillMode="flat"
                        size="small"
                        themeColor="error"
                        disabled={isCancelling || isResending}
                        onClick={() => handleCancel(invite)}
                      >
                        Cancel
                      </CPButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    </CPActionsheetModal>
  );
};

export default PendingInvitesPanel;
